import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { useRecentAccounts } from '@hooks/useRecentAccounts';
import { useBiometricLabel } from '@hooks/useBiometricLabel';
import { usePublicBrand } from '@hooks/usePublicBrand';
import { RecentAccountCard } from './login/RecentAccountCard';
import * as Icons from '@components/Icons';
import type { RecentAccount } from '@api/types';

const passkeysSupported = typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';

/**
 * "Hace X" en español, granularidad coarse: minutos / horas / días / semanas.
 * Se usa para "Último acceso: hace 2 horas" del saludo principal.
 */
/** Devuelve la primera palabra significativa del nombre, o `null` si no aplica. */
function firstName(fullName: string | null): string | null {
  const trimmed = fullName?.trim();
  if (!trimmed) return null;
  const [first] = trimmed.split(/\s+/);
  return first.length > 0 ? first : null;
}

function timeAgo(isoTimestamp: string): string {
  const then = Date.parse(isoTimestamp);
  if (Number.isNaN(then)) return '';
  const diffMs = Math.max(0, Date.now() - then);
  const m = Math.floor(diffMs / 60_000);
  if (m < 1) return 'un momento';
  if (m < 60) return `${String(m)} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${String(h)} ${h === 1 ? 'hora' : 'horas'}`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${String(d)} ${d === 1 ? 'día' : 'días'}`;
  const w = Math.floor(d / 7);
  return `${String(w)} ${w === 1 ? 'semana' : 'semanas'}`;
}

export function AdminLogin() {
  const navigate = useNavigate();
  const {
    requestMagicLink,
    signInWithPasskey,
    trySignInOrMagic,
    isAuthenticated,
    isLoading,
    error,
  } = useAuth();
  const { accounts: recentAccounts, remove: removeRecent } = useRecentAccounts();
  const biometricLabel = useBiometricLabel();
  const brand = usePublicBrand();

  // Tipamos explícitamente `RecentAccount | undefined` porque el tsconfig no
  // tiene `noUncheckedIndexedAccess` y `recentAccounts[0]` sale como
  // `RecentAccount` no opcional. El componente usa `primary ? … : …`.
  const primary: RecentAccount | undefined = useMemo(
    () => (recentAccounts.length > 0 ? recentAccounts[0] : undefined),
    [recentAccounts],
  );
  const others = useMemo(() => recentAccounts.slice(1), [recentAccounts]);

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [localError, setLocalError] = useState('');
  const [busy, setBusy] = useState(false);
  // Cuando hay una cuenta guardada (primary), el form de email queda
  // oculto por defecto para que la tarjeta sea el único CTA visible. El
  // link "¿Otro email?" lo despliega. Sin primary, el form se ve directo.
  const [showForm, setShowForm] = useState(false);

  // Autocomplete propio sobre el input de email — sugiere cuentas recientes
  // del navegador (useRecentAccounts). Coexiste con `autoComplete="email"`
  // para que Bitwarden siga funcionando. Aparece on-focus con lista completa
  // y filtra por substring mientras el usuario teclea.
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const emailWrapperRef = useRef<HTMLDivElement>(null);

  const filteredSuggestions = useMemo<readonly RecentAccount[]>(() => {
    const q = email.trim().toLowerCase();
    if (!q) return recentAccounts;
    return recentAccounts.filter(
      (acc) =>
        acc.email.toLowerCase().includes(q) ||
        (acc.name?.toLowerCase().includes(q) ?? false),
    );
  }, [recentAccounts, email]);

  // Click-outside cierra el dropdown.
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      const wrap = emailWrapperRef.current;
      if (!wrap || wrap.contains(e.target as Node)) return;
      setShowSuggestions(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => { document.removeEventListener('mousedown', onMouseDown); };
  }, []);

  // Reset del highlight al cambiar el contenido visible del dropdown.
  useEffect(() => { setHighlightedIdx(-1); }, [showSuggestions, email]);

  function pickSuggestion(acc: RecentAccount) {
    setEmail(acc.email);
    setShowSuggestions(false);
  }

  function handleEmailKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!showSuggestions || filteredSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx((idx) => Math.min(idx + 1, filteredSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx((idx) => Math.max(idx - 1, 0));
    } else if (e.key === 'Enter' && highlightedIdx >= 0) {
      e.preventDefault();
      const acc = filteredSuggestions[highlightedIdx];
      pickSuggestion(acc);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      void navigate('/admin/bookings', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  async function handleContinuePrimary() {
    setLocalError('');
    setBusy(true);
    try {
      await signInWithPasskey();
    } catch {
      setLocalError('No pudimos validar tu passkey. Prueba con el email.');
    } finally {
      setBusy(false);
    }
  }

  async function handleContinueOther(targetEmail: string) {
    setLocalError('');
    setBusy(true);
    try {
      const passkeyOk = await trySignInOrMagic(targetEmail);
      if (!passkeyOk) {
        setSent(true);
        setSentEmail(targetEmail);
      }
    } catch {
      setLocalError('No pudimos enviar el enlace. Inténtalo de nuevo.');
    } finally {
      setBusy(false);
    }
  }

  async function handleMagicLink(e: React.SyntheticEvent) {
    e.preventDefault();
    setLocalError('');
    const value = email.trim();
    if (!value) return;
    setBusy(true);
    try {
      await requestMagicLink(value);
      setSent(true);
      setSentEmail(value);
    } catch {
      setLocalError('No pudimos enviar el enlace. Inténtalo de nuevo.');
    } finally {
      setBusy(false);
    }
  }

  async function handleContinueButton() {
    setLocalError('');
    const value = email.trim();
    if (!value) return;
    setBusy(true);
    try {
      const passkeyOk = await trySignInOrMagic(value);
      if (!passkeyOk) {
        setSent(true);
        setSentEmail(value);
      }
    } catch {
      setLocalError('No pudimos continuar. Inténtalo de nuevo.');
    } finally {
      setBusy(false);
    }
  }

  const brandingSide = (
    <aside className="login-side">
      <div className="login-brand">
        <span className="side-brand-mark">E</span>
        <span>Equipomag Bookings</span>
      </div>

      <div className="login-hero">
        <div className="login-hero-eyebrow">PANEL DE ADMINISTRACIÓN · v3.0</div>
        <h1 className="login-hero-title">
          Gestiona tus centros, agenda y clientes desde un solo lugar.
        </h1>
        <p className="login-hero-body">
          Sistema multi-centro con asignaciones por rol, aprobación manual de solicitudes,
          recordatorios automáticos y backup diario sobre Cloudflare D1.
        </p>
      </div>

      <div className="login-foot">MADRID · BARCELONA · VALENCIA · EQUIPOMAG</div>
    </aside>
  );

  if (sent) {
    return (
      <div className="login-page">
        {brandingSide}
        <div className="login-v2">
          <div className="login-v2-inner">
            <h1 className="login-greeting-title">Revisa tu correo</h1>
            <p className="login-greeting-sub">
              Si <strong>{sentEmail}</strong> tiene acceso, te hemos enviado un enlace para entrar. Caduca en 30 minutos.
            </p>
            <button
              type="button"
              className="btn"
              data-variant="ghost"
              onClick={() => { setSent(false); setSentEmail(''); }}
            >
              Usar otro correo
            </button>
            <p className="login-foot-v2">¿No te llega? Revisa spam o pídele acceso a tu administrador.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      {brandingSide}
      <div className="login-v2">
        <div className="login-v2-inner">
        {primary ? (
          <>
            <h1 className="login-greeting-title">
              Hola, {firstName(primary.name) ?? primary.email}
            </h1>
            <p className="login-greeting-sub">
              Último acceso: hace {timeAgo(primary.lastLoginAt)}. Continúa con tu passkey o cambia de cuenta.
            </p>
          </>
        ) : (
          <>
            <h1 className="login-greeting-title">Acceder al panel</h1>
            <p className="login-greeting-sub">
              Introduce tu email para continuar. Te enviaremos un enlace seguro.
            </p>
          </>
        )}

        {(localError || error) && (
          <div className="login-error" role="alert">
            {localError !== '' ? localError : error}
          </div>
        )}

        {primary && passkeysSupported && (
          <RecentAccountCard
            account={primary}
            variant="primary"
            biometricLabel={biometricLabel}
            onClick={() => { void handleContinuePrimary(); }}
            disabled={busy || isLoading}
          />
        )}

        {others.length > 0 && (
          <>
            <div className="login-divider-otras">
              <span className="login-divider-line" aria-hidden />
              <span className="login-divider-label">OTRAS CUENTAS</span>
              <span className="login-divider-line" aria-hidden />
            </div>
            <div className="login-others">
              {others.map((acc) => (
                <RecentAccountCard
                  key={acc.email}
                  account={acc}
                  variant="secondary"
                  onClick={() => { void handleContinueOther(acc.email); }}
                  onRemove={() => { removeRecent(acc.email); }}
                  disabled={busy || isLoading}
                />
              ))}
            </div>
          </>
        )}

        {/* Form de email — siempre visible si no hay cuenta guardada.
            Con cuenta guardada queda oculto por defecto detrás del link
            "¿Otro email?" para que la tarjeta sea el único CTA. */}
        {(!primary || showForm) && (
          <>
            <div className="login-divider-thin" aria-hidden />
            <form className="login-form-v2" onSubmit={handleMagicLink} noValidate>
              <label className="field-label" htmlFor="login-email-v2">Email</label>
              <div ref={emailWrapperRef} style={{ position: 'relative' }}>
                <input
                  id="login-email-v2"
                  className="input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => { setShowSuggestions(true); }}
                  onKeyDown={handleEmailKeyDown}
                  placeholder={primary?.email ?? 'tu@centromag.com'}
                  data-testid="login-email"
                  autoFocus={primary ? showForm : false}
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={showSuggestions && filteredSuggestions.length > 0}
                  aria-controls="login-email-suggestions"
                  aria-activedescendant={
                    highlightedIdx >= 0 && filteredSuggestions[highlightedIdx]
                      ? `login-suggestion-${filteredSuggestions[highlightedIdx].email}`
                      : undefined
                  }
                />
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div
                    id="login-email-suggestions"
                    className="cust-ac-dropdown"
                    role="listbox"
                    data-testid="login-email-suggestions"
                  >
                    {filteredSuggestions.map((acc, i) => {
                      const active = i === highlightedIdx;
                      return (
                        <button
                          key={acc.email}
                          id={`login-suggestion-${acc.email}`}
                          type="button"
                          className="cust-ac-row"
                          role="option"
                          aria-selected={active}
                          style={active ? { background: 'var(--bg-soft)' } : undefined}
                          onMouseDown={(e) => { e.preventDefault(); }}
                          onClick={() => { pickSuggestion(acc); }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="cust-ac-row-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {acc.email}
                            </div>
                            <div className="cust-ac-row-meta">
                              {acc.name ? `${acc.name} · ` : ''}hace {timeAgo(acc.lastLoginAt)}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="login-form-row">
                <button
                  type="submit"
                  className="btn"
                  data-variant={primary ? 'primary' : 'outline'}
                  data-size="lg"
                  disabled={busy || isLoading || !email.trim()}
                  data-testid="login-magic-link"
                >
                  <Icons.Mail size={14} /> {primary ? 'Enviar enlace' : 'Magic link'}
                </button>
                {/* "Continuar →" solo aparece cuando NO hay primary — con
                    primary la tarjeta de arriba ya cumple ese rol y este
                    botón duplicaría el CTA. */}
                {!primary && (
                  <button
                    type="button"
                    className="btn"
                    data-variant="primary"
                    data-size="lg"
                    disabled={busy || isLoading || !email.trim()}
                    onClick={() => { void handleContinueButton(); }}
                    data-testid="login-continue"
                  >
                    Continuar →
                  </button>
                )}
              </div>
            </form>
          </>
        )}

        {/* Link "¿Otro email?" — solo cuando hay primary y el form
            no está desplegado. Click despliega el form. */}
        {primary && !showForm && (
          <button
            type="button"
            className="login-other-email-link"
            onClick={() => { setShowForm(true); }}
            data-testid="login-other-email"
          >
            ¿Otro email?
          </button>
        )}

        <p className="login-foot-v2">
          Acceso restringido al equipo.
          {brand?.www && (
            <>
              {' '}¿Eres cliente? Visita{' '}
              <a href={brand.www} target="_blank" rel="noreferrer noopener">
                {brand.name}
              </a>
              .
            </>
          )}
        </p>
        </div>
      </div>
    </div>
  );
}
